import { NestedAgency, NestedSearch, SearchTab, TODO } from "@habity/types";
import { getCategoryTextPlural } from "./addresses";
// import { getFiltersFromServerSearchParams } from "./searches";
import { getNameFromLabel } from "./labels";

// Extended filter type that includes joined area/municipality data from database
type MetadataFilters = Partial<NestedSearch> & {
  areas?: Array<{ id: number; name: string; municipality?: { id: number; name: string } }>;
  municipalities?: Array<{ id: number; name: string }>;
};

export const getMetadataTitle = (
  agency: NestedAgency | null,
  filters: MetadataFilters | null,
  tab?: SearchTab,
  streetWithCity?: string,
  customStartTitle?: string[]
) => {
  const { statuses: appStatuses } = agency || {};
  const {
    streets,
    categories,
    zips,
    city,
    label,
    // places,
    statuses,
    longitude,
    latitude,
    areas,
    municipalities,
  } = filters || {};

  // Check if there's an active search (has location or other filters)
  const hasActiveSearch = !!(
    streets || city || areas?.length || municipalities?.length || 
    zips || (longitude && latitude) || label
  );

  // Use "Hitta" prefix only when no active search, otherwise just capitalize the noun
  const title = customStartTitle || (hasActiveSearch ? [] : ["Hitta"]);

  const categoryName =
    categories && categories.length > 0
      ? categories?.map((c: TODO) => getCategoryTextPlural(c)).join(", ")
      : "";

  if (!tab || tab === "homes") {
    // Add category or "bostäder" first
    if (categories && categories.length > 0) {
      // Capitalize first letter if no "Hitta" prefix
      const catText = hasActiveSearch && title.length === 0 
        ? categoryName.charAt(0).toUpperCase() + categoryName.slice(1)
        : categoryName;
      title.push(catText);
    } else {
      title.push(hasActiveSearch && title.length === 0 ? "Bostäder" : "bostäder");
    }
    // Add statuses after (e.g. "bostäder till salu")
    const status = statuses?.map(
      (s) => appStatuses?.find((as) => as.name === s)?.title
    );
    if (status) title.push(status.join(" och ").toLowerCase());
  } else if (tab === "speculators") {
    title.push(hasActiveSearch && title.length === 0 ? "Spekulanter" : "spekulanter");
    if (categories && categories.length > 0) {
      title.push(`av ${categoryName}`);
    }
  } else if (tab === "brokers") {
    title.push(hasActiveSearch && title.length === 0 ? "Mäklare" : "mäklare");
    if (categories && categories.length > 0) {
      title.push(`för ${categoryName}`);
    }
  } else if (tab === "areas") {
    title.push(hasActiveSearch && title.length === 0 ? "Områden" : "områden");
  } else if (tab === "projects") {
    title.push(hasActiveSearch && title.length === 0 ? "Nyproduktion" : "nyproduktion");
  }

  // Build location name from various sources (returns just the location, no preposition)
  const getLocationName = () => {
    if (streetWithCity) {
      return streetWithCity[0] + (streetWithCity.length > 1 ? `, ${streetWithCity[1]}` : "");
    }
    if (streets) {
      return Array.isArray(streets) ? streets.join(" och ") : streets;
    }
    if (city) {
      return city;
    }
    // Check for areas (nested objects with name property from database joins)
    // Group by municipality to avoid repetition: "Södermalm och Vasastan, Stockholm"
    if (areas && Array.isArray(areas) && areas.length > 0) {
      const validAreas = areas.filter((a: TODO) => typeof a === 'object' && a?.name);
      if (validAreas.length > 0) {
        // Group areas by municipality
        const municipalityGroups = new Map<string, string[]>();
        const areasWithoutMunicipality: string[] = [];
        
        for (const area of validAreas) {
          const municipalityName = area.municipality?.name;
          if (municipalityName) {
            if (!municipalityGroups.has(municipalityName)) {
              municipalityGroups.set(municipalityName, []);
            }
            municipalityGroups.get(municipalityName)!.push(area.name);
          } else {
            areasWithoutMunicipality.push(area.name);
          }
        }
        
        // Build result: "Södermalm och Vasastan, Stockholm" or "Söder, Varberg"
        // If area name equals municipality name, just show one (e.g. "Simrishamn" not "Simrishamn, Simrishamn")
        const parts: string[] = [];
        for (const [municipality, areaNames] of municipalityGroups) {
          const uniqueAreaNames = areaNames.filter(name => name !== municipality);
          if (uniqueAreaNames.length === 0) {
            parts.push(municipality);
          } else {
            parts.push(`${uniqueAreaNames.join(" och ")}, ${municipality}`);
          }
        }
        if (areasWithoutMunicipality.length > 0) {
          parts.push(areasWithoutMunicipality.join(" och "));
        }
        
        if (parts.length > 0) {
          return parts.join(" och ");
        }
      }
    }
    // Check for municipalities (nested objects with name property from database joins)
    if (municipalities && Array.isArray(municipalities) && municipalities.length > 0) {
      const munNames = municipalities
        .map((m: TODO) => (typeof m === 'object' && m?.name ? m.name : null))
        .filter((name): name is string => typeof name === 'string' && name.length > 0);
      if (munNames.length > 0) {
        return munNames.join(" och ");
      }
    }
    if (zips) {
      return `postkod ${Array.isArray(zips) ? zips.join(" och ") : zips}`;
    }
    if (longitude && latitude) {
      return "nära mig";
    }
    if (label) {
      return getNameFromLabel(label) || label;
    }
    return null;
  };

  const locationName = getLocationName();
  // : nearby && nearby.length > 0
  // ? ` nära ${nearby.join(' och ')}`

  const baseTitle = title.join(" ");
  return locationName ? `${baseTitle}, ${locationName}` : `${baseTitle} i Sverige`;
};
